import React, { Children, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Skeleton } from '@fs/zion-ui'
import { usePageViewEvent } from '@fs/zion-analytics'
import { useOneOffPageData } from '../../hooks'
import { getCustomPageFlavor } from '../helpers'
import BrightspotIdMetaTag from '../BrightspotIdMetaTag'
import { PageLead } from './lead/PageLead'
import PageContent from './content/PageContent'
import SeoPageHead from '../../components/header/SeoPageHead'
import ResponsiveSectionBand from '../ResponsiveSectionBand'
import { flavors } from '../../config'

/**
 * Default loading skeleton that matches PageLead + PageContent layout
 */
const DefaultSkeleton = () => (
  <>
    <ResponsiveSectionBand>
      <Skeleton.H1 />
      <Skeleton.Paragraph lineCount={2} />
    </ResponsiveSectionBand>
    <ResponsiveSectionBand>
      <Skeleton.H2 />
      <Skeleton.Paragraph lineCount={4} />
    </ResponsiveSectionBand>
  </>
)

/**
 * Helper: Wrap a React component as ClientContent for PageContent rendering
 */
const wrapAsClientContent = (component) => ({
  __typename: 'ClientContent',
  Component: component,
})

/**
 * Helper: Separate children by type (populate, insert, regular)
 */
const categorizeChildren = (children) => {
  const childrenArray = Children.toArray(children)
  return {
    populateChildren: childrenArray.filter((child) => child.props?.populateAt !== undefined),
    injectableChildren: childrenArray.filter((child) => child.props?.insertAt !== undefined),
    regularChildren: childrenArray.filter(
      (child) => child.props?.insertAt === undefined && child.props?.populateAt === undefined
    ),
  }
}

/**
 * Helper: Populate custom components with Brightspot content at specific indices
 */
const applyPopulateChildren = (content, populateChildren) => {
  const result = [...content]
  populateChildren.forEach((child) => {
    const index = child.props.populateAt
    if (index >= 0 && index < result.length) {
      const originalContent = result[index]
      result[index] = wrapAsClientContent(React.cloneElement(child, { originalContent }))
    }
  })
  return result
}

/**
 * Helper: Insert children at specified positions
 */
const applyInsertChildren = (content, injectableChildren) => {
  if (injectableChildren.length === 0) return content

  const result = [...content]
  // Sort by insertAt index (-1 means end)
  const sorted = [...injectableChildren].sort((a, b) => {
    const aIndex = a.props.insertAt === -1 ? Infinity : a.props.insertAt
    const bIndex = b.props.insertAt === -1 ? Infinity : b.props.insertAt
    return aIndex - bIndex
  })

  sorted.forEach((child) => {
    const index = child.props.insertAt === -1 ? result.length : child.props.insertAt
    result.splice(index, 0, wrapAsClientContent(child))
  })

  return result
}

/**
 * Helper: Build final content array with children injection and population
 */
const buildContent = (data, children, ignoreContent) => {
  if (ignoreContent) return []

  const content = data?.content || []
  if (!children) return content

  const { populateChildren, injectableChildren, regularChildren } = categorizeChildren(children)

  // Step 1: Handle population
  let result = applyPopulateChildren(content, populateChildren)

  // Step 2: Handle insertions
  result = applyInsertChildren(result, injectableChildren)

  // Step 3: Append regular children
  const wrappedRegular = regularChildren.map(wrapAsClientContent)
  return [...result, ...wrappedRegular]
}

/**
 * Helper: Merge prop title/description with Brightspot data for PageLead
 * Props take priority; Brightspot data used as fallback
 */
const buildPageLeadData = (data, title, description) => ({
  ...data,
  title: title || data?.title,
  name: title || data?.name,
  description: description || data?.description,
})

/**
 * A standardized page component for rendering Brightspot-driven pages.
 * Fetches data from Brightspot and renders with PageLead + PageContent layout.
 *
 * @param {Object} props
 * @param {string} props.path - The Brightspot path to fetch page data from
 * @param {string} [props.pageDetail] - Custom analytics page_detail value. Falls back to data.seo.analyticsPageName or path
 * @param {string} [props.title] - Title for PageLead. Takes priority over Brightspot data. Brightspot data used as fallback
 * @param {string} [props.description] - Description for PageLead. Takes priority over Brightspot data. Brightspot data used as fallback
 * @param {React.ReactNode} [props.structuredData] - Structured data component (e.g., FAQPageStructuredData). Data prop is automatically injected
 * @param {string} [props.flavor] - Page flavor (e.g., 'blueberry', 'seafoam'). Brightspot colorScheme takes priority; this is used as fallback
 * @param {React.ReactNode} [props.banner] - Custom banner component. If provided, ribbon is hidden
 * @param {React.ReactNode} [props.children] - Custom content to inject/replace in PageContent. Children with insertAt prop will be injected at that index. Children with populateAt prop will replace Brightspot content at that index and receive originalContent prop
 * @param {React.ReactNode} [props.clientLead] - Custom PageLead component to replace default. Brightspot data prop is automatically injected (same as structuredData)
 * @param {boolean} [props.ignoreContent=false] - Skip PageContent rendering and only render children
 * @param {React.ReactNode} [props.skeleton] - Custom loading skeleton. If not provided, uses default skeleton matching PageLead + PageContent layout
 * @param {React.ReactNode} [props.notFound] - Rendered when Brightspot returns no data for the path. If not provided, renders null
 */
export default function OneOffPage({
  path,
  pageDetail,
  title,
  description,
  structuredData,
  flavor: flavorProp,
  banner,
  children,
  clientLead,
  ignoreContent = false,
  skeleton,
  notFound = null,
}) {
  const { loading, data } = useOneOffPageData({ path })
  const sendPageView = usePageViewEvent()

  // Track page view only after data is loaded to ensure accurate analytics
  useEffect(() => {
    if (!loading && data) {
      sendPageView({
        page_detail: data?.seo?.analyticsPageName || pageDetail || path,
      })
    }
  }, [loading, data, pageDetail, path, sendPageView])

  // Loading state - use custom skeleton or default
  if (loading) {
    return skeleton || <DefaultSkeleton />
  }

  // No data state
  if (!data) {
    return notFound
  }

  // Determine flavor from prop or Brightspot colorScheme
  const flavor = getCustomPageFlavor(data?.colorScheme) || flavorProp || flavors.default

  // Build content array with children injection and population
  const enhancedContent = buildContent(data, children, ignoreContent)

  // Build PageLead data with title/description (props take priority)
  const pageLeadData = buildPageLeadData(data, title, description)

  // Determine if ribbon should be shown (hidden if banner is provided)
  const noRibbon = Boolean(banner)

  // Clone structuredData and automatically inject data prop
  const enhancedStructuredData =
    structuredData && React.isValidElement(structuredData) ? React.cloneElement(structuredData, { data }) : null

  // Clone clientLead and automatically inject data prop (same pattern as structuredData)
  const enhancedClientLead =
    clientLead && React.isValidElement(clientLead) ? React.cloneElement(clientLead, { data }) : null

  return (
    <>
      <SeoPageHead data={data} />
      <BrightspotIdMetaTag id={data?.id} />
      {enhancedStructuredData}
      {banner}
      {enhancedClientLead || <PageLead data={pageLeadData} flavor={flavor} noRibbon={noRibbon} />}
      {ignoreContent ? children : <PageContent content={enhancedContent} flavor={flavor} />}
    </>
  )
}

OneOffPage.propTypes = {
  path: PropTypes.string.isRequired,
  pageDetail: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  structuredData: PropTypes.node,
  flavor: PropTypes.string,
  banner: PropTypes.node,
  children: PropTypes.node,
  clientLead: PropTypes.node,
  ignoreContent: PropTypes.bool,
  skeleton: PropTypes.node,
  notFound: PropTypes.node,
}
